/**
 * Utilities and constants for BufferSyncContext
 *
 * Pure functions extracted from BufferSyncContext.tsx.
 *
 * @module @humanizer/studio/contexts/buffer-sync-utils
 */

import type { BufferSummary } from './buffer-sync-types';

// ═══════════════════════════════════════════════════════════════════════════
// CONSTANTS
// ═══════════════════════════════════════════════════════════════════════════

export const DEFAULT_BUFFER_NAME = 'workspace';
export const POLL_INTERVAL_MS = 2000; // Poll for changes every 2 seconds
export const TAB_SESSION_KEY = 'humanizer-tab-session-id';

// ═══════════════════════════════════════════════════════════════════════════
// UTILITIES
// ═══════════════════════════════════════════════════════════════════════════

/** Clean a buffer name: strip markup artifacts, extensions, truncate */
export function cleanBufferName(raw: string, fallback = ''): string {
  let name = raw;
  // Strip HTML comment markers (thread serialization)
  name = name.replace(/<!--[^>]*-->\n?/g, '');
  // Strip leading markdown headers
  name = name.replace(/^#+\s*/, '');
  // Handle markdown image links: ![alt](url) → alt text or "Image"
  const imgMatch = name.match(/^!\[([^\]]*)\]\(([^)]+)\)/);
  if (imgMatch) {
    const alt = imgMatch[1].trim();
    if (alt) {
      name = alt;
    } else {
      // Extract domain from URL as fallback
      try {
        const domain = new URL(imgMatch[2]).hostname.replace(/^www\./, '');
        name = `Image (${domain})`;
      } catch {
        name = 'Image';
      }
    }
  }
  // Handle markdown links: [text](url) → text
  name = name.replace(/\[([^\]]+)\]\([^)]+\)/g, '$1');
  // Strip standalone URLs that are the entire name
  if (/^https?:\/\/\S+$/.test(name.trim())) {
    try {
      const url = new URL(name.trim());
      const path = url.pathname.split('/').filter(Boolean).pop() ?? '';
      name = path ? decodeURIComponent(path) : url.hostname.replace(/^www\./, '');
    } catch {
      // keep as-is
    }
  }
  // Strip file extensions
  name = name.replace(/\.(md|txt|json|html)$/i, '');
  // Strip leading JSON bracket artifacts
  name = name.replace(/^[\[\]{}\s]+/, '');
  // Collapse whitespace and trim
  name = name.replace(/\s+/g, ' ').trim();
  // Truncate at 50 chars
  if (name.length > 50) {
    name = name.slice(0, 47) + '...';
  }
  return name || fallback;
}

/** Map an Electron ContentBuffer to BufferSummary for the picker */
export function electronBufferToSummary(buf: {
  id: string;
  text: string;
  wordCount: number;
  state: string;
  origin: { metadata?: Record<string, unknown>; sourcePlatform?: string };
  createdAt: number;
  updatedAt: number;
}): BufferSummary {
  const userTitle = buf.origin.metadata?.title as string | undefined;
  const threadTitle = buf.origin.metadata?.threadTitle as string | undefined;
  // Derive a meaningful name: user title > threadTitle > first line of text > UUID prefix
  let name: string;
  if (userTitle) {
    name = userTitle;
  } else if (threadTitle) {
    name = cleanBufferName(threadTitle, buf.id.slice(0, 8));
  } else if (buf.text) {
    const cleanText = buf.text.replace(/<!--[^>]*-->\n?/g, '').trim();
    const firstLine = cleanText.split('\n')[0]?.trim() ?? '';
    name = cleanBufferName(firstLine, buf.id.slice(0, 8));
  } else {
    name = buf.id.slice(0, 8);
  }

  // Recompute word count from text if stored count looks wrong
  const textWordCount = buf.text ? buf.text.replace(/<!--[^>]*-->/g, '').split(/\s+/).filter(Boolean).length : 0;
  const wordCount = textWordCount > 0 ? textWordCount : buf.wordCount;

  return {
    id: buf.id,
    name,
    version: buf.state,
    currentBranch: 'main',
    contentLength: wordCount,
    createdAt: new Date(buf.createdAt).toISOString(),
    updatedAt: new Date(buf.updatedAt).toISOString(),
  };
}
